/**
 * Turns a filled-in translation catalog (.xlsx) into the JSON payload for the
 * `Institution.custom_i18n_overrides` backend setting.
 *
 * Deliberately literal: the payload is built only from what a person wrote in the
 * "Nuevo texto" columns. Glossary references are expanded when the catalog is
 * updated (see `i18nCatalogUpdate`), so by the time a file gets here every text has
 * been reviewed — a leftover `{{glossary:...}}` means the row skipped that review
 * and is rejected rather than shipped.
 *
 * The Glosario sheet yields overrides like any other. Its keys live in
 * `commonGlossary.json` and the locale files reference them through i18next nesting, so
 * overriding a term reaches every text that references it. That matters because a renamed
 * entity otherwise only lands on the rows somebody rewrote by hand, and every text left
 * alone would keep the old term — silently out of step with the rest of the app. A row
 * rewritten by hand still wins: it replaces the whole sentence, nesting included.
 *
 * Every entry is validated before it makes it into the payload, since a bad override
 * is only noticed at runtime — the key has to exist in that locale, its interpolation
 * variables have to survive the rewrite, and its main key has to be resolvable by
 * `applyLocaleOverrides`.
 */

import type { Workbook } from "exceljs";
import { getMainKeysByFile } from "@config/i18n/files";
import {
  buildGlossaryRows,
  CATALOG_COLUMNS,
  COLUMNS_BY_LOCALE,
  extractInterpolationVars,
  flattenLocales,
  getCellText,
  INSTRUCTIONS_SHEET_NAME,
  loadExcelJs,
  LOCALES,
  type LocaleCode,
  type LocalesByCode,
} from "./i18nCatalog";
import { hasGlossaryReference } from "./i18nGlossary";

export type OverrideWarningType =
  | "unknown-key"
  | "interpolation-mismatch"
  | "unresolvable-main-key"
  | "unresolved-glossary";

export interface OverrideWarning {
  type: OverrideWarningType;
  locale: LocaleCode;
  fullKey: string;
  sheetName: string;
  rowNumber: number;
  message: string;
}

export interface OverridesStats {
  esCount: number;
  enCount: number;
  skippedCount: number;
  /** Entries that matched the current text exactly and were left out of the payload. */
  unchangedCount: number;
}

export interface OverridesResult {
  overrides: Partial<Record<LocaleCode, Record<string, any>>>;
  warnings: OverrideWarning[];
  stats: OverridesStats;
}

export const OVERRIDES_FILE_NAME = "i18n-overrides.json";

const LOCALE_LABELS: Record<LocaleCode, string> = { es: "ES", en: "EN" };

function setNestedValue(target: Record<string, any>, dotKey: string, value: string): void {
  const parts = dotKey.split(".");
  let current = target;

  for (let index = 0; index < parts.length - 1; index++) {
    if (!current[parts[index]]) current[parts[index]] = {};
    current = current[parts[index]];
  }

  current[parts[parts.length - 1]] = value;
}

interface LocaleIndex {
  messages: Record<string, string>;
  resolvableMainKeys: Set<string>;
}

function buildLocaleIndex(locales: LocalesByCode): Record<LocaleCode, LocaleIndex> {
  const mainKeysByFile = getMainKeysByFile();
  const flat = flattenLocales(locales);
  const glossaryRows = buildGlossaryRows(locales);

  // `flattenLocales` models the texts a user reads, so it leaves the glossary out. Those
  // terms are overridable keys all the same, and without them here every Glosario row
  // would be turned away as an unknown key.
  const glossaryTerms = (locale: LocaleCode): Record<string, string> =>
    Object.fromEntries(
      glossaryRows
        .map(row => [row.fullKey, locale === "es" ? row.currentEs : row.currentEn] as const)
        // A term the locale file does not define cannot be overridden in it.
        .filter(([, term]) => !!term),
    );

  // `applyLocaleOverrides` resolves a main key in two steps: it looks up the locale
  // file, then reads the section out of it. Both have to succeed, and the lookup
  // table is keyed off the ES files, so an EN-only section is not resolvable.
  const toIndex = (locale: LocaleCode): LocaleIndex => ({
    messages: { ...flat[locale], ...glossaryTerms(locale) },
    resolvableMainKeys: new Set(
      Object.keys(mainKeysByFile).filter(mainKey => !!mainKeysByFile[mainKey]?.[locale]?.[mainKey]),
    ),
  });

  return { es: toIndex("es"), en: toIndex("en") };
}

interface EntryContext {
  locale: LocaleCode;
  fullKey: string;
  newValue: string;
  sheetName: string;
  rowNumber: number;
  index: LocaleIndex;
}

/** Returns the warning that disqualifies the entry, or null when it is safe to apply. */
function validateEntry({
  locale,
  fullKey,
  newValue,
  sheetName,
  rowNumber,
  index,
}: EntryContext): OverrideWarning | null {
  const label = LOCALE_LABELS[locale];
  const base = { locale, fullKey, sheetName, rowNumber };
  const currentValue = index.messages[fullKey];

  if (currentValue === undefined) {
    return {
      ...base,
      type: "unknown-key",
      message: `[${label}] La key no existe en los textos actuales: "${fullKey}"`,
    };
  }

  if (!index.resolvableMainKeys.has(fullKey.split(".")[0])) {
    return {
      ...base,
      type: "unresolvable-main-key",
      message:
        `[${label}] La key "${fullKey}" pertenece a un archivo que no está registrado en ` +
        `config/i18n/files.ts, así que la app no podría aplicar el override`,
    };
  }

  if (hasGlossaryReference(newValue)) {
    return {
      ...base,
      type: "unresolved-glossary",
      message:
        `[${label}] "${fullKey}" tiene referencias {{glossary:...}} sin resolver, y un texto así ` +
        "se mostraría literal en pantalla. Pasa el archivo por el paso 1: ahí se reemplazan por " +
        "el término del glosario y se marcan en Notas las que haya que revisar",
    };
  }

  const expectedVars = extractInterpolationVars(currentValue);
  const foundVars = extractInterpolationVars(newValue);

  if (expectedVars.join("|") !== foundVars.join("|")) {
    return {
      ...base,
      type: "interpolation-mismatch",
      message:
        `[${label}] Las variables no coinciden en "${fullKey}": se esperaban ` +
        `${expectedVars.join(", ") || "(ninguna)"} y se encontraron ` +
        `${foundVars.join(", ") || "(ninguna)"}`,
    };
  }

  return null;
}

/** Reads a filled-in catalog and builds the override payload, skipping invalid entries. */
export async function parseCatalogBuffer(
  buffer: ArrayBuffer,
  locales: LocalesByCode,
): Promise<OverridesResult> {
  const ExcelJS = await loadExcelJs();
  const workbook: Workbook = new ExcelJS.Workbook();

  await workbook.xlsx.load(buffer);

  const localeIndex = buildLocaleIndex(locales);
  const overrides: OverridesResult["overrides"] = {};
  const warnings: OverrideWarning[] = [];
  const counts: Record<LocaleCode, number> = { es: 0, en: 0 };
  let unchangedCount = 0;

  workbook.eachSheet(sheet => {
    if (sheet.name === INSTRUCTIONS_SHEET_NAME) return;

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const fullKey = getCellText(row.getCell(CATALOG_COLUMNS.fullKey));

      if (!fullKey) return;

      LOCALES.forEach(locale => {
        const newValue = getCellText(row.getCell(COLUMNS_BY_LOCALE[locale].newText));

        if (!newValue) return;

        const index = localeIndex[locale];
        const warning = validateEntry({
          locale,
          fullKey,
          newValue,
          sheetName: sheet.name,
          rowNumber,
          index,
        });

        if (warning) {
          warnings.push(warning);

          return;
        }

        // A text identical to the current one only bloats the setting.
        if (index.messages[fullKey] === newValue) {
          unchangedCount++;

          return;
        }

        if (!overrides[locale]) overrides[locale] = {};

        setNestedValue(overrides[locale], fullKey, newValue);
        counts[locale]++;
      });
    });
  });

  return {
    overrides,
    warnings,
    stats: {
      esCount: counts.es,
      enCount: counts.en,
      skippedCount: warnings.length,
      unchangedCount,
    },
  };
}

export async function parseCatalogFile(
  file: File,
  locales: LocalesByCode,
): Promise<OverridesResult> {
  return parseCatalogBuffer(await file.arrayBuffer(), locales);
}
